import { useEffect, useMemo, useState } from "react";
import { api, formatError } from "@/lib/api";
import { Card } from "./Dashboard";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { toast } from "sonner";
import { Download, Building2, Users, User, Calendar, Tag } from "lucide-react";
import { PieChart, Pie, Cell, ResponsiveContainer, Tooltip, BarChart, Bar, XAxis, YAxis } from "recharts";
import { useAuth } from "@/context/AuthContext";

const SCOPES = [
  { key: "company", label: "Company", icon: <Building2 className="h-4 w-4"/> },
  { key: "team", label: "Team", icon: <Users className="h-4 w-4"/> },
  { key: "employee", label: "Employee", icon: <User className="h-4 w-4"/> },
  { key: "monthly", label: "Monthly", icon: <Calendar className="h-4 w-4"/> },
  { key: "assignment", label: "By Assignment", icon: <Tag className="h-4 w-4"/> },
];

const today = new Date();
const isoDate = (d) => {
  const y = d.getFullYear();
  const m = String(d.getMonth() + 1).padStart(2, "0");
  const day = String(d.getDate()).padStart(2, "0");
  return `${y}-${m}-${day}`;
};
const monthStart = new Date(today.getFullYear(), today.getMonth(), 1);

const minToHours = (m) => (Math.round((m||0)/6)/10);
const pctOrNA = (v) => (v === null || v === undefined) ? <span className="text-slate-400">N/A</span> : `${(v*100).toFixed(0)}%`;

export default function ReportsPage() {
  const { user } = useAuth();
  const [scope, setScope] = useState(user.role === "team_admin" ? "team" : "company");
  const [from, setFrom] = useState(isoDate(monthStart));
  const [to, setTo] = useState(isoDate(today));
  const [year, setYear] = useState(today.getFullYear());
  const [month, setMonth] = useState(today.getMonth() + 1);
  const [teams, setTeams] = useState([]);
  const [employees, setEmployees] = useState([]);
  const [categories, setCategories] = useState([]);
  const [teamId, setTeamId] = useState("");
  const [employeeId, setEmployeeId] = useState("");
  const [categoryId, setCategoryId] = useState("");
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    (async () => {
      const [t, e, c] = await Promise.all([
        api.get("/teams"), api.get("/employees"), api.get("/categories"),
      ]);
      setTeams(t.data); setEmployees(e.data); setCategories(c.data);
      if (e.data[0] && !employeeId) setEmployeeId(e.data[0].employee_id);
      if (t.data[0] && !teamId) setTeamId(t.data[0].team_id);
      if (c.data[0] && !categoryId) setCategoryId(c.data[0].category_id);
    })();
  // eslint-disable-next-line
  }, []);

  const fetchReport = async () => {
    setLoading(true);
    try {
      let res;
      if (scope === "company") res = await api.get("/reports/company", { params: { from_date: from, to_date: to } });
      else if (scope === "team") res = await api.get("/reports/team", { params: { team_id: teamId, from_date: from, to_date: to } });
      else if (scope === "employee") res = await api.get("/reports/employee", { params: { employee_id: employeeId, from_date: from, to_date: to } });
      else if (scope === "monthly") res = await api.get("/reports/monthly", { params: { year, month, team_id: teamId || undefined } });
      else if (scope === "assignment") res = await api.get("/reports/by-assignment", { params: { category_id: categoryId, from_date: from, to_date: to } });
      setData(res.data);
    } catch (e) {
      toast.error(formatError(e.response?.data?.detail));
    } finally { setLoading(false); }
  };

  useEffect(() => { fetchReport(); /* eslint-disable-next-line */ }, [scope, from, to, year, month, teamId, employeeId, categoryId]);

  const exportXlsx = async () => {
    try {
      let params = {};
      if (scope === "monthly") {
        const a = new Date(year, month - 1, 1);
        const b = new Date(year, month, 0);
        params = { from_date: isoDate(a), to_date: isoDate(b) };
        if (teamId) params.team_id = teamId;
      } else if (scope === "team") {
        params = { from_date: from, to_date: to, team_id: teamId };
      } else {
        params = { from_date: from, to_date: to };
      }
      const r = await api.get("/export/utilisation.xlsx", { params, responseType: "blob" });
      const url = URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a"); a.href = url;
      a.download = `Utilisation_${params.from_date}_${params.to_date}.xlsx`;
      document.body.appendChild(a); a.click(); a.remove();
      URL.revokeObjectURL(url);
    } catch (e) { toast.error(formatError(e.response?.data?.detail)); }
  };

  return (
    <div>
      <div className="flex items-end justify-between mb-6 flex-wrap gap-4">
        <div>
          <div className="eyebrow">Analytics</div>
          <h1 className="font-display text-4xl mt-1">Utilisation reports</h1>
          <p className="text-sm text-[#4B5773] mt-2 max-w-xl">Hours by assignment, employee, team, company and month. Three lenses on utilisation, side by side.</p>
        </div>
        <Button onClick={exportXlsx} variant="outline" data-testid="export-util-btn"
          className="rounded-lg border-slate-200"><Download className="h-4 w-4 mr-2"/>Export to Excel</Button>
      </div>

      <div className="flex flex-wrap gap-2 mb-4">
        {SCOPES.map(s => (
          <button key={s.key} onClick={()=>setScope(s.key)} data-testid={`scope-${s.key}`}
            className={`flex items-center gap-2 px-4 py-2 rounded-lg text-sm font-medium transition-colors ${
              scope === s.key ? "bg-[#2563EB] text-white" : "bg-white border border-slate-200 text-[#4B5773] hover:bg-slate-50"
            }`}>{s.icon}{s.label}</button>
        ))}
      </div>

      <Card>
        <div className="grid grid-cols-1 md:grid-cols-5 gap-3">
          {scope !== "monthly" && (
            <>
              <FilterField label="From"><Input type="date" value={from} data-testid="rep-from"
                onChange={(e)=>setFrom(e.target.value)} className="h-10 rounded-lg"/></FilterField>
              <FilterField label="To"><Input type="date" value={to} data-testid="rep-to"
                onChange={(e)=>setTo(e.target.value)} className="h-10 rounded-lg"/></FilterField>
            </>
          )}
          {scope === "monthly" && (
            <>
              <FilterField label="Year">
                <Select value={String(year)} onValueChange={(v)=>setYear(Number(v))}>
                  <SelectTrigger className="h-10 rounded-lg" data-testid="rep-year"><SelectValue/></SelectTrigger>
                  <SelectContent>{[2024,2025,2026].map(y => <SelectItem key={y} value={String(y)}>{y}</SelectItem>)}</SelectContent>
                </Select>
              </FilterField>
              <FilterField label="Month">
                <Select value={String(month)} onValueChange={(v)=>setMonth(Number(v))}>
                  <SelectTrigger className="h-10 rounded-lg" data-testid="rep-month"><SelectValue/></SelectTrigger>
                  <SelectContent>
                    {["Jan","Feb","Mar","Apr","May","Jun","Jul","Aug","Sep","Oct","Nov","Dec"].map((m,i)=>(
                      <SelectItem key={m} value={String(i+1)}>{m}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </FilterField>
            </>
          )}
          {(scope === "team" || scope === "monthly") && (
            <FilterField label="Team">
              <Select value={teamId || "all"} onValueChange={(v)=>setTeamId(v === "all" ? "" : v)}>
                <SelectTrigger className="h-10 rounded-lg" data-testid="rep-team"><SelectValue/></SelectTrigger>
                <SelectContent>
                  {scope === "monthly" && <SelectItem value="all">All teams</SelectItem>}
                  {teams.map(t => <SelectItem key={t.team_id} value={t.team_id}>{t.name}</SelectItem>)}
                </SelectContent>
              </Select>
            </FilterField>
          )}
          {scope === "employee" && (
            <FilterField label="Employee">
              <Select value={employeeId} onValueChange={setEmployeeId}>
                <SelectTrigger className="h-10 rounded-lg" data-testid="rep-employee"><SelectValue/></SelectTrigger>
                <SelectContent>{employees.map(e => <SelectItem key={e.employee_id} value={e.employee_id}>{e.name}</SelectItem>)}</SelectContent>
              </Select>
            </FilterField>
          )}
          {scope === "assignment" && (
            <FilterField label="Category">
              <Select value={categoryId} onValueChange={setCategoryId}>
                <SelectTrigger className="h-10 rounded-lg" data-testid="rep-category"><SelectValue/></SelectTrigger>
                <SelectContent>{categories.map(c => <SelectItem key={c.category_id} value={c.category_id}>{c.name}</SelectItem>)}</SelectContent>
              </Select>
            </FilterField>
          )}
        </div>
      </Card>

      <div className="mt-6">
        {loading && <div className="text-center text-[#4B5773] py-12">Loading…</div>}
        {!loading && data && scope !== "assignment" && <StandardReport data={data} scope={scope} allCategories={categories} />}
        {!loading && data && scope === "assignment" && <AssignmentReport data={data} />}
      </div>
    </div>
  );
}

function FilterField({ label, children }) {
  return (
    <div>
      <Label className="eyebrow">{label}</Label>
      <div className="mt-1">{children}</div>
    </div>
  );
}

function StandardReport({ data, scope, allCategories = [] }) {
  const util = data.utilisation || {};
  const totals = data.totals || {};
  const cats = data.categories || [];
  const emps = data.employees || [];

  const workCats = cats.filter(c => c.kind === "work");
  // Matrix columns: all active work categories then all active leave categories
  // (stable order regardless of whether data was logged this period).
  const matrixWork = allCategories.filter(c => c.kind === "work" && c.is_active).sort((a,b)=>a.name.localeCompare(b.name));
  const matrixLeave = allCategories.filter(c => c.kind === "leave" && c.is_active).sort((a,b)=>a.name.localeCompare(b.name));
  const matrixCols = [...matrixWork, ...matrixLeave];
  const showMatrix = emps.length > 0 && (scope === "monthly" || scope === "company" || scope === "team") && matrixCols.length > 0;
  const total = workCats.reduce((s,c)=>s+c.minutes, 0) || 1;

  return (
    <div className="space-y-6">
      <div className="grid grid-cols-1 md:grid-cols-3 gap-4" data-testid="util-stats">
        <UtilCard label="% of scheduled" value={util.scheduled}
          hint="Work ÷ shift hours"
          unavailableHint="No shifts scheduled in this period."/>
        <UtilCard label="% of contracted" value={util.contracted}
          hint="Work ÷ working-pattern hours"
          unavailableHint="No contracted working days in this period."/>
        <UtilCard label="% of working time" value={util.working_time}
          hint="Work ÷ (shift − leave − BH)"
          unavailableHint="No available working time - leave and bank holidays equal or exceed scheduled hours."/>
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-3 gap-4">
        <Card title="Totals">
          <ul className="text-sm space-y-2">
            <Row label="Scheduled" value={minToHours(totals.shift_minutes)} unit="h"/>
            <Row label="Allocated" value={minToHours(totals.allocated_minutes)} unit="h"/>
            <Row label="Work" value={minToHours(totals.work_minutes)} unit="h" emphasis/>
            <Row label="Leave" value={minToHours(totals.leave_minutes)} unit="h"/>
            <Row label="Bank Holiday" value={minToHours(totals.bank_holiday_minutes)} unit="h"/>
            <Row label="Contracted pattern" value={minToHours(totals.pattern_minutes)} unit="h"/>
          </ul>
        </Card>
        <Card title="Category mix (work only)">
          <div className="h-56">
            {workCats.length === 0 ? (
              <div className="grid place-items-center h-full text-sm text-[#4B5773]">No work allocations in this period.</div>
            ) : (
              <ResponsiveContainer width="100%" height="100%">
                <PieChart>
                  <Pie data={workCats} dataKey="minutes" nameKey="name" outerRadius={75} innerRadius={45} paddingAngle={2}>
                    {workCats.map((c, i) => <Cell key={i} fill={c.color} />)}
                  </Pie>
                  <Tooltip formatter={(v) => `${minToHours(v)}h`} />
                </PieChart>
              </ResponsiveContainer>
            )}
          </div>
        </Card>
        <Card title="All categories">
          <ul className="space-y-2 max-h-56 overflow-y-auto">
            {cats.map(c => (
              <li key={c.category_id} className="flex items-center justify-between text-sm">
                <div className="flex items-center gap-2 min-w-0">
                  <span className="h-2.5 w-2.5 rounded-full" style={{ backgroundColor: c.color }}/>
                  <span className="truncate">{c.name}</span>
                  <span className="text-[10px] uppercase tracking-widest text-slate-400">{c.kind}</span>
                </div>
                <span className="tabular-nums text-slate-700">{minToHours(c.minutes)}h <span className="text-slate-400">· {(c.share*100).toFixed(0)}%</span></span>
              </li>
            ))}
            {!cats.length && <li className="text-sm text-[#4B5773]">No allocations.</li>}
          </ul>
        </Card>
      </div>

      {emps.length > 0 && (
        <Card title={`Per-employee breakdown (${emps.length})`}>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="util-emp-table">
              <thead>
                <tr className="text-left text-[#4B5773]">
                  <th className="py-3 eyebrow font-normal">Employee</th>
                  <th className="py-3 eyebrow font-normal text-right">Shift</th>
                  <th className="py-3 eyebrow font-normal text-right">Work</th>
                  <th className="py-3 eyebrow font-normal text-right">Leave</th>
                  <th className="py-3 eyebrow font-normal text-right">BH</th>
                  <th className="py-3 eyebrow font-normal text-right">% Scheduled</th>
                  <th className="py-3 eyebrow font-normal text-right">% Contracted</th>
                  <th className="py-3 eyebrow font-normal text-right">% Working</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {emps.map(e => (
                  <tr key={e.employee_id}>
                    <td className="py-2.5 font-medium">{e.name}</td>
                    <td className="py-2.5 text-right tabular-nums">{minToHours(e.shift_minutes)}h</td>
                    <td className="py-2.5 text-right tabular-nums font-medium">{minToHours(e.work_minutes)}h</td>
                    <td className="py-2.5 text-right tabular-nums text-slate-500">{minToHours(e.leave_minutes)}h</td>
                    <td className="py-2.5 text-right tabular-nums text-slate-500">{minToHours(e.bank_holiday_minutes)}h</td>
                    <td className="py-2.5 text-right tabular-nums">{pctOrNA(e.utilisation.scheduled)}</td>
                    <td className="py-2.5 text-right tabular-nums">{pctOrNA(e.utilisation.contracted)}</td>
                    <td className="py-2.5 text-right tabular-nums">{pctOrNA(e.utilisation.working_time)}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </Card>
      )}

      {showMatrix && (
        <Card title={`Per-employee × category matrix (${emps.length})`}
              data-testid="util-matrix-card">
          <p className="text-xs text-[#4B5773] mb-3">
            Hours each employee logged against every assignment category - including unallocated rows shown as 0h. Use this for monthly utilisation reports per agent.
          </p>
          <div className="overflow-x-auto">
            <table className="min-w-full text-sm" data-testid="util-matrix-table">
              <thead>
                <tr className="text-left text-[#4B5773] border-b border-slate-200">
                  <th className="py-3 pr-4 eyebrow font-normal sticky left-0 bg-white">Employee</th>
                  {matrixCols.map(c => (
                    <th key={c.category_id}
                        className="py-3 px-2 eyebrow font-normal text-right whitespace-nowrap"
                        data-testid={`matrix-col-${c.category_id}`}>
                      <span className="inline-flex items-center gap-1.5">
                        <span className="inline-block h-2 w-2 rounded-full" style={{ backgroundColor: c.color }}/>
                        {c.name}
                      </span>
                    </th>
                  ))}
                  <th className="py-3 px-2 eyebrow font-normal text-right whitespace-nowrap">Bank Holiday</th>
                  <th className="py-3 px-2 eyebrow font-normal text-right whitespace-nowrap bg-[#F6F9FE]">Total Work</th>
                  <th className="py-3 px-2 eyebrow font-normal text-right whitespace-nowrap bg-[#F6F9FE]">Total Leave</th>
                  <th className="py-3 px-2 eyebrow font-normal text-right whitespace-nowrap bg-[#F6F9FE]">Total Allocated</th>
                </tr>
              </thead>
              <tbody className="divide-y divide-slate-100">
                {emps.map(e => {
                  const byCat = e.by_category || {};
                  return (
                    <tr key={e.employee_id} data-testid={`matrix-row-${e.employee_id}`}>
                      <td className="py-2.5 pr-4 font-medium sticky left-0 bg-white whitespace-nowrap">{e.name}</td>
                      {matrixCols.map(c => {
                        const m = byCat[c.category_id] || 0;
                        return (
                          <td key={c.category_id}
                              className={`py-2.5 px-2 text-right tabular-nums ${m === 0 ? "text-slate-300" : ""}`}
                              data-testid={`matrix-cell-${e.employee_id}-${c.category_id}`}>
                            {minToHours(m)}h
                          </td>
                        );
                      })}
                      <td className={`py-2.5 px-2 text-right tabular-nums ${e.bank_holiday_minutes === 0 ? "text-slate-300" : "text-slate-500"}`}>
                        {minToHours(e.bank_holiday_minutes)}h
                      </td>
                      <td className="py-2.5 px-2 text-right tabular-nums font-medium bg-[#F6F9FE]">{minToHours(e.work_minutes)}h</td>
                      <td className="py-2.5 px-2 text-right tabular-nums bg-[#F6F9FE]">{minToHours(e.leave_minutes)}h</td>
                      <td className="py-2.5 px-2 text-right tabular-nums font-medium bg-[#F6F9FE]">{minToHours(e.allocated_minutes)}h</td>
                    </tr>
                  );
                })}
              </tbody>
              <tfoot>
                <tr className="border-t-2 border-slate-200 font-medium text-[#1F2A44]">
                  <td className="py-2.5 pr-4 sticky left-0 bg-white">Company total</td>
                  {matrixCols.map(c => {
                    const tot = emps.reduce((s,e) => s + ((e.by_category||{})[c.category_id] || 0), 0);
                    return (
                      <td key={c.category_id} className={`py-2.5 px-2 text-right tabular-nums ${tot === 0 ? "text-slate-300" : ""}`}>
                        {minToHours(tot)}h
                      </td>
                    );
                  })}
                  <td className="py-2.5 px-2 text-right tabular-nums text-slate-500">
                    {minToHours(emps.reduce((s,e)=>s+(e.bank_holiday_minutes||0), 0))}h
                  </td>
                  <td className="py-2.5 px-2 text-right tabular-nums bg-[#F6F9FE]">
                    {minToHours(emps.reduce((s,e)=>s+(e.work_minutes||0), 0))}h
                  </td>
                  <td className="py-2.5 px-2 text-right tabular-nums bg-[#F6F9FE]">
                    {minToHours(emps.reduce((s,e)=>s+(e.leave_minutes||0), 0))}h
                  </td>
                  <td className="py-2.5 px-2 text-right tabular-nums bg-[#F6F9FE]">
                    {minToHours(emps.reduce((s,e)=>s+(e.allocated_minutes||0), 0))}h
                  </td>
                </tr>
              </tfoot>
            </table>
          </div>
        </Card>
      )}
    </div>
  );
}

function AssignmentReport({ data }) {
  const target = data.employees || [];
  const totalH = minToHours(data.totals?.category_minutes || 0);
  return (
    <Card title={`${data.category?.name || "Assignment"} - ${totalH}h across ${target.length} employees`}>
      <div className="h-64 mb-4">
        <ResponsiveContainer>
          <BarChart data={target.slice(0, 10).map(e => ({ name: e.name, hours: minToHours(e.category_minutes) }))}>
            <XAxis dataKey="name" tick={{ fontSize: 11 }}/>
            <YAxis tick={{ fontSize: 11 }}/>
            <Tooltip formatter={(v) => `${v}h`}/>
            <Bar dataKey="hours" fill={data.category?.color || "#2563EB"} radius={[4,4,0,0]}/>
          </BarChart>
        </ResponsiveContainer>
      </div>
      <table className="min-w-full text-sm">
        <thead>
          <tr className="text-left text-[#4B5773]">
            <th className="py-3 eyebrow font-normal">Employee</th>
            <th className="py-3 eyebrow font-normal text-right">Hours on assignment</th>
            <th className="py-3 eyebrow font-normal text-right">Share of work time</th>
          </tr>
        </thead>
        <tbody className="divide-y divide-slate-100">
          {target.map(e => (
            <tr key={e.employee_id}>
              <td className="py-2.5 font-medium">{e.name}</td>
              <td className="py-2.5 text-right tabular-nums">{minToHours(e.category_minutes)}h</td>
              <td className="py-2.5 text-right tabular-nums text-slate-500">
                {e.work_minutes ? `${((e.category_minutes/e.work_minutes)*100).toFixed(0)}%` : "-"}
              </td>
            </tr>
          ))}
          {!target.length && <tr><td colSpan={3} className="py-8 text-center text-[#4B5773]">Nobody allocated time to this category.</td></tr>}
        </tbody>
      </table>
    </Card>
  );
}

function UtilCard({ label, value, hint, unavailableHint }) {
  const unavailable = value === null || value === undefined;
  const pct = unavailable ? null : ((value || 0) * 100).toFixed(1);
  return (
    <div className="bg-white border border-slate-200 rounded-xl p-5">
      <div className="eyebrow">{label}</div>
      {unavailable ? (
        <>
          <div className="font-display text-4xl mt-2 mb-1 text-slate-400">N/A</div>
          <div className="text-[11px] text-slate-500">{unavailableHint || "Not enough data to compute this metric."}</div>
        </>
      ) : (
        <>
          <div className="font-display text-4xl mt-2 mb-1">{pct}<span className="text-2xl text-[#4B5773]">%</span></div>
          <div className="text-[11px] text-[#4B5773]">{hint}</div>
          <div className="mt-3 h-1.5 w-full rounded-full bg-slate-100 overflow-hidden">
            <div className="h-full bg-[#2563EB]" style={{ width: `${Math.min(100, value*100 || 0)}%` }} />
          </div>
        </>
      )}
    </div>
  );
}

function Row({ label, value, unit, emphasis }) {
  return (
    <li className="flex justify-between items-baseline">
      <span className={`text-[#4B5773] ${emphasis ? "" : ""}`}>{label}</span>
      <span className={`tabular-nums ${emphasis ? "font-display text-lg" : ""}`}>{value}{unit}</span>
    </li>
  );
}
